import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CompassIcon, LayersIcon, CpuIcon, UsersIcon, TrendingUpIcon } from 'lucide-react';
import { Card, CardBody } from './ui/Card';
const pillars = [{
  icon: CompassIcon,
  title: 'Leadership Philosophy',
  content: 'I lead at the intersection of systems thinking and human empathy. Effective enterprise leadership requires the ability to hold strategic vision and granular execution simultaneously — building teams that can operate with autonomy while maintaining coherent direction. My approach centers on psychological safety, clear decision frameworks, and a relentless focus on outcomes over outputs.'
}, {
  icon: LayersIcon,
  title: 'Governance & Design Systems Architecture',
  content: 'Scalable organizations require scalable design. I architect governance frameworks that transform design from a service function into a strategic capability — establishing token systems, component libraries, and contribution models that allow distributed teams to move fast without fragmenting the brand. My systems have governed experiences across federal agencies, global retail platforms, and multi-division enterprises.'
}, {
  icon: CpuIcon,
  title: 'Enterprise AI Integration',
  content: 'AI is not a feature — it is an architectural layer. I lead organizations through responsible AI integration by establishing governance guardrails, identifying high-leverage automation opportunities, and ensuring human-centered design principles remain central. From generative content workflows to AI-assisted accessibility auditing, I bridge the gap between emerging capability and enterprise readiness.'
}, {
  icon: UsersIcon,
  title: 'Cultural Transformation Through Brand Cohesion',
  content: 'Brand is organizational culture made visible. When I lead rebranding initiatives, I treat them as change management programs — engaging stakeholders across divisions, building internal brand champions, and creating governance structures that sustain alignment long after launch. The result is not just visual consistency, but organizational identity that attracts talent and builds customer trust.'
}, {
  icon: TrendingUpIcon,
  title: 'Omni-Channel Experience Strategy',
  content: 'Modern customers do not experience channels — they experience brands. I design experience ecosystems that maintain coherent identity across digital, physical, and service touchpoints. By aligning customer journey mapping with business capability modeling, I ensure that every interaction reinforces brand promise and drives measurable commercial outcomes.'
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    x: -20
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function ExecutiveMode() {
  return <div data-id="element-397">
      {/* Section header */}
      <div className="mb-12" data-id="element-398">
        <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-399">
          <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-400" />
          Executive View — Leadership Narrative
        </span>
        <h2 className="font-heading font-bold text-[#1C1F23] text-3xl lg:text-4xl mb-4" data-id="element-401">
          How I Lead & Why It Works
        </h2>
        <p className="font-body text-[#4A5A66] max-w-2xl" data-id="element-402">
          A philosophy built on systems thinking, governance architecture, and
          the belief that great design is ultimately a leadership discipline —
          not a craft function.
        </p>
      </div>

      {/* Philosophy pillars */}
      <motion.div className="space-y-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-50px'
    }} data-id="element-403">
        {pillars.map(pillar => {
        const Icon = pillar.icon;
        return <motion.div key={pillar.title} variants={itemVariants} data-id="element-404">
              <Card bordered data-id="element-405">
                <CardBody data-id="element-406">
                  <div className="flex gap-5" data-id="element-407">
                    <div className="w-12 h-12 rounded-xl bg-[#2F3A45] flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-408">
                      <Icon className="w-5 h-5 text-[#B08D57]" data-id="element-409" />
                    </div>
                    <div data-id="element-410">
                      <h3 className="font-heading font-semibold text-[#1C1F23] text-lg mb-2" data-id="element-411">
                        {pillar.title}
                      </h3>
                      <p className="font-body font-light text-[#4A5A66] leading-relaxed text-sm" data-id="element-412">
                        {pillar.content}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </motion.div>;
      })}
      </motion.div>
    </div>;
}